import { useState, useEffect, memo } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { fetchCountries } from "../lib/api";
import { visitedCountries as fallbackVisited, countryProjects as fallbackProjects } from "../data/mock";
import { X, MapPin } from "lucide-react";

const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const WorldMap = memo(() => {
  const [tooltipContent, setTooltipContent] = useState("");
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [visited, setVisited] = useState(fallbackVisited);
  const [projects, setProjects] = useState(fallbackProjects);

  useEffect(() => {
    fetchCountries()
      .then((data) => {
        if (data.length > 0) {
          const v = {};
          const p = {};
          data.forEach((c) => {
            v[c.country_code] = c.name;
            p[c.country_code] = c.project_details;
          });
          setVisited(v);
          setProjects(p);
        }
      })
      .catch(() => { /* use fallback */ });
  }, []);

  return (
    <div className="relative" data-testid="world-map">
      {tooltipContent && !selectedCountry && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 pointer-events-none">
          <span className="bg-[#1C2B3A] text-white text-sm px-4 py-1.5 rounded-md shadow-lg font-sans whitespace-nowrap">
            {tooltipContent}
          </span>
        </div>
      )}

      {selectedCountry && projects[selectedCountry.id] && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 w-[90%] max-w-sm">
          <div className="bg-white rounded-lg shadow-xl border border-[#E5DDD3] p-5 animate-fade-up" data-testid="map-popup">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <MapPin size={16} className="text-[#B8945A]" />
                <h4 className="font-serif text-lg font-bold text-[#1C2B3A]">
                  {selectedCountry.name}
                </h4>
              </div>
              <button
                onClick={() => setSelectedCountry(null)}
                className="text-[#7A7A7A] hover:text-[#1C2B3A] transition-colors p-0.5"
                data-testid="map-popup-close"
              >
                <X size={16} />
              </button>
            </div>
            <p className="font-sans text-sm text-[#4A4A4A] leading-relaxed">
              {projects[selectedCountry.id]}
            </p>
          </div>
        </div>
      )}

      <ComposableMap
        projectionConfig={{ rotate: [-10, 0, 0], scale: 147 }}
        style={{ width: "100%", height: "auto" }}
        className="outline-none"
      >
        <Geographies geography={geoUrl}>
          {({ geographies }) =>
            geographies.map((geo) => {
              const countryName = visited[geo.id];
              const isVisited = !!countryName;
              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  onMouseEnter={() => {
                    if (isVisited) setTooltipContent(countryName);
                  }}
                  onMouseLeave={() => setTooltipContent("")}
                  onClick={() => {
                    if (isVisited) {
                      setSelectedCountry({ id: geo.id, name: countryName });
                    }
                  }}
                  style={{
                    default: {
                      fill: isVisited ? "#B8945A" : "#E5DDD3",
                      stroke: "#FAF8F4",
                      strokeWidth: 0.5,
                      outline: "none",
                    },
                    hover: {
                      fill: isVisited ? "#96783E" : "#D5CFC7",
                      stroke: "#FAF8F4",
                      strokeWidth: 0.5,
                      outline: "none",
                      cursor: isVisited ? "pointer" : "default",
                    },
                    pressed: {
                      fill: isVisited ? "#96783E" : "#D5CFC7",
                      outline: "none",
                    },
                  }}
                />
              );
            })
          }
        </Geographies>
      </ComposableMap>
    </div>
  );
});

WorldMap.displayName = "WorldMap";
export default WorldMap;
